import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUsageMetrics } from '@/hooks/useUsageMetrics';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Users, 
  Clock, 
  Activity, 
  Monitor,
  TrendingUp,
  FileText
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface UsageMetricsProps {
  dateRange?: {
    from: Date | null;
    to: Date | null;
  };
}

function formatMinutes(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
}

function getPageLabel(path: string): string {
  const labels: Record<string, string> = {
    '/': 'Dashboard',
    '/admin': 'Admin',
    '/leads': 'Leads',
    '/deals': 'Deals',
    '/contacts': 'Contatos',
    '/cases': 'Cases',
    '/tasks': 'Tarefas',
    '/whatsapp': 'WhatsApp',
    '/calls': 'Chamadas',
    '/settings': 'Configurações',
  };
  
  // Check for partial matches
  for (const [key, label] of Object.entries(labels)) {
    if (path.startsWith(key) && key !== '/') {
      return label;
    }
  }
  
  return labels[path] || path;
}

export function UsageMetrics({ dateRange }: UsageMetricsProps) {
  const { t } = useTranslation();
  const { 
    userMetrics, 
    activeSessions, 
    pageMetrics, 
    hourlyMetrics, 
    summary, 
    isLoading 
  } = useUsageMetrics(dateRange);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Online Agora
            </CardTitle>
            <Activity className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.onlineNow}</div>
            <p className="text-xs text-muted-foreground">usuários ativos</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Tempo Total
            </CardTitle>
            <Clock className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatMinutes(summary.totalMinutesToday)}</div>
            <p className="text-xs text-muted-foreground">no período</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Sessões
            </CardTitle>
            <Monitor className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.totalSessions}</div>
            <p className="text-xs text-muted-foreground">total de sessões</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Média por Sessão
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatMinutes(summary.avgSessionMinutes)}</div>
            <p className="text-xs text-muted-foreground">tempo médio</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active Sessions */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-green-500" />
              Usuários Online
            </CardTitle>
            <CardDescription>
              {activeSessions.length} usuário{activeSessions.length !== 1 ? 's' : ''} ativo{activeSessions.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              <div className="space-y-3">
                {activeSessions.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Nenhum usuário online
                  </p>
                ) : (
                  activeSessions.map((session) => (
                    <div 
                      key={session.sessionId} 
                      className="flex items-center gap-3 p-2 rounded-lg bg-muted/50"
                    >
                      <div className="relative">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={session.avatarUrl || undefined} />
                          <AvatarFallback className="text-xs">
                            {session.userName.slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-background" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{session.userName}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {getPageLabel(session.currentPath)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* User Usage Table */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Tempo de Uso por Usuário
            </CardTitle>
            <CardDescription>
              Ranking de uso no período selecionado
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuário</TableHead>
                    <TableHead className="text-right">Tempo Total</TableHead>
                    <TableHead className="text-right">Sessões</TableHead>
                    <TableHead className="text-right">Média</TableHead>
                    <TableHead className="text-right">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userMetrics.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        Sem dados de uso no período
                      </TableCell>
                    </TableRow>
                  ) : (
                    userMetrics.slice(0, 20).map((user) => (
                      <TableRow key={user.userId}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={user.avatarUrl || undefined} />
                              <AvatarFallback className="text-xs">
                                {user.userName.slice(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-sm">{user.userName}</p>
                              <p className="text-xs text-muted-foreground">{user.userEmail}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatMinutes(user.totalMinutes)}
                        </TableCell>
                        <TableCell className="text-right">
                          {user.sessionsCount}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatMinutes(user.avgSessionMinutes)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant={user.isOnline ? "default" : "secondary"}>
                            {user.isOnline ? 'Online' : 'Offline'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hourly Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Distribuição por Horário
            </CardTitle>
            <CardDescription>
              Usuários ativos por hora do dia
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hourlyMetrics}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="hour" 
                    tickFormatter={(h) => `${h}h`}
                    className="text-xs"
                  />
                  <YAxis className="text-xs" />
                  <Tooltip 
                    formatter={(value: number) => [`${value} usuários`, 'Ativos']}
                    labelFormatter={(h) => `${h}:00 - ${h}:59`}
                  />
                  <Bar 
                    dataKey="activeUsers" 
                    fill="hsl(var(--primary))" 
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top Pages */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Páginas Mais Acessadas
            </CardTitle>
            <CardDescription>
              Módulos mais utilizados no período
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[250px]">
              <div className="space-y-3">
                {pageMetrics.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Sem dados de navegação
                  </p>
                ) : (
                  pageMetrics.map((page, index) => (
                    <div 
                      key={page.path} 
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-muted-foreground w-6">
                          {index + 1}
                        </span>
                        <div>
                          <p className="font-medium">{getPageLabel(page.path)}</p>
                          <p className="text-xs text-muted-foreground">{page.path}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{page.visits} visitas</p>
                        <p className="text-xs text-muted-foreground">
                          {page.uniqueUsers} usuário{page.uniqueUsers !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
